/**
 * Users list — staff, dealer-agent, system principals scoped to the
 * caller's country. Customer users are intentionally excluded; they
 * live under /customers (KYC-managed, not IAM-managed).
 *
 * Inline "Invite user" form for callers with iam.user.write.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  ApiError,
  type CreateIamUserRequest,
  type IamRole,
  type IamUser,
  type IamUserListRow,
} from "@dfp/api-client";
import { Badge } from "../components/Badge.js";
import { Button } from "../components/Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { Table, TBody, Td, Th, THead, Tr } from "../components/Table.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";

export function UsersPage(): JSX.Element {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const canCreate = (auth.user?.permissions ?? []).includes("iam.user.write");

  const list = useQuery({
    queryKey: ["iam", "users", "list"],
    queryFn: () => api.get<readonly IamUserListRow[]>("/api/iam/users"),
  });
  const roles = useQuery({
    queryKey: ["iam", "roles"],
    queryFn: () => api.get<readonly IamRole[]>("/api/iam/roles"),
  });

  const [showInvite, setShowInvite] = useState(false);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Users</h1>
        <p className="text-sm text-slate-500">
          Staff, dealer agents, and service principals. Customer accounts live under{" "}
          <Link to="/customers" className="text-brand-600 hover:underline">
            Customers
          </Link>
          .
        </p>
      </div>

      {canCreate ? (
        <Card>
          <CardHeader>
            <CardTitle>Invite user</CardTitle>
            {!showInvite ? <Button onClick={() => setShowInvite(true)}>New user</Button> : null}
          </CardHeader>
          {showInvite ? (
            <CardBody>
              <InviteUserForm
                roles={roles.data ?? []}
                onCreated={(u) => {
                  void queryClient.invalidateQueries({ queryKey: ["iam", "users", "list"] });
                  setShowInvite(false);
                  // Pre-fill the cache entry so the detail page hydrates instantly.
                  queryClient.setQueryData(["iam", "users", "detail", u.id], {
                    user: u,
                    roles: [],
                    effectivePermissions: [],
                  });
                }}
                onCancel={() => setShowInvite(false)}
              />
            </CardBody>
          ) : null}
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>All users</CardTitle>
          <span className="text-xs text-slate-500">{list.data?.length ?? 0} rows</span>
        </CardHeader>
        <CardBody className="p-0">
          {list.isLoading ? (
            <p className="p-5 text-sm text-slate-500">Loading…</p>
          ) : list.isError ? (
            <p className="p-5 text-sm text-red-700">Failed to load users.</p>
          ) : (list.data ?? []).length === 0 ? (
            <p className="p-5 text-sm text-slate-500">No users in this country yet.</p>
          ) : (
            <Table>
              <THead>
                <tr>
                  <Th>Display name</Th>
                  <Th>Type</Th>
                  <Th>Contact</Th>
                  <Th>Status</Th>
                  <Th>Password</Th>
                  <Th>Roles</Th>
                  <Th>Last login</Th>
                </tr>
              </THead>
              <TBody>
                {(list.data ?? []).map((u) => (
                  <Tr key={u.id}>
                    <Td>
                      <Link
                        to="/iam/users/$id"
                        params={{ id: u.id }}
                        className="font-medium text-brand-600 hover:underline"
                      >
                        {u.displayName}
                      </Link>
                      <div className="font-mono text-[10px] text-slate-400">
                        {u.id.slice(0, 8)}…
                      </div>
                    </Td>
                    <Td>
                      <Badge tone={u.type === "staff" ? "brand" : "neutral"}>{u.type}</Badge>
                    </Td>
                    <Td className="text-sm text-slate-700">
                      {u.email ?? u.phone ?? <span className="text-slate-400">—</span>}
                    </Td>
                    <Td>
                      <StatusBadge status={u.status} />
                    </Td>
                    <Td className="text-xs">
                      {/*
                        Three states:
                          - Rotation pending — must_change_password=true on the user row
                          - "—" — no password (OTP-only auth, e.g. dealer agents)
                          - last-changed timestamp when known; "set" otherwise
                      */}
                      {u.mustChangePassword ? (
                        <Badge tone="warning">Rotation pending</Badge>
                      ) : u.email === null ? (
                        <span className="text-slate-400">OTP-only</span>
                      ) : u.passwordChangedAt ? (
                        <span className="text-slate-600">
                          changed {new Date(u.passwordChangedAt).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-slate-400">set</span>
                      )}
                    </Td>
                    <Td className="text-sm">{u.activeRoleCount}</Td>
                    <Td className="text-xs text-slate-600">
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : "never"}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function StatusBadge({ status }: { status: IamUser["status"] }): JSX.Element {
  switch (status) {
    case "active":
      return <Badge tone="success">active</Badge>;
    case "suspended":
      return <Badge tone="warning">suspended</Badge>;
    case "locked_out":
      return <Badge tone="danger">locked</Badge>;
    case "deleted":
      return <Badge tone="neutral">deleted</Badge>;
  }
}

function InviteUserForm({
  roles,
  onCreated,
  onCancel,
}: {
  roles: readonly IamRole[];
  onCreated: (u: IamUser) => void;
  onCancel: () => void;
}): JSX.Element {
  const [type, setType] = useState<"staff" | "dealer_agent">("staff");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [roleKeys, setRoleKeys] = useState<readonly string[]>([]);
  const [dealerId, setDealerId] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Dealer list — pulled lazily so the form opens fast for staff
  // creation (the dropdown only matters for dealer_agent). The query
  // never errors fatally; we surface a hint instead.
  const dealersQuery = useQuery({
    queryKey: ["dealers", "list"],
    queryFn: () => api.get<readonly { id: string; name: string }[]>("/api/dealers"),
    enabled: type === "dealer_agent",
  });

  // Hide approval-requiring roles from the inline form — those route
  // through the approvals workflow instead (Approvals page).
  const assignable = useMemo(() => roles.filter((r) => !r.requiresApproval), [roles]);

  const mutation = useMutation({
    mutationFn: (body: CreateIamUserRequest) => api.post<IamUser>("/api/iam/users", body),
    onSuccess: (created) => {
      onCreated(created);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setError(err.problem.message ?? err.problem.detail ?? "Create failed");
      } else {
        setError("Network error");
      }
    },
  });

  function submit(): void {
    setError(null);
    if (displayName.trim().length < 2) {
      setError("Display name is required.");
      return;
    }
    if (!email && !phone) {
      setError("Either email or phone is required.");
      return;
    }
    if (type === "staff" && password.length < 12) {
      setError("Staff users need an initial password of at least 12 characters.");
      return;
    }
    if (type === "dealer_agent" && password.length > 0 && password.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    if (type === "dealer_agent" && dealerId.length === 0) {
      setError("Dealer agents must be linked to a dealer.");
      return;
    }
    const body: CreateIamUserRequest = {
      type,
      displayName: displayName.trim(),
      ...(email ? { email: email.trim() } : {}),
      ...(phone ? { phone: phone.trim() } : {}),
      ...(password ? { password } : {}),
      ...(roleKeys.length > 0 ? { roleKeys } : {}),
      ...(type === "dealer_agent" ? { dealerId } : {}),
    };
    mutation.mutate(body);
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Type">
          <select
            value={type}
            onChange={(e) => setType(e.target.value as "staff" | "dealer_agent")}
            className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="staff">Staff (email + password)</option>
            <option value="dealer_agent">Dealer agent (phone, OTP-friendly)</option>
          </select>
        </Field>
        <Field label="Display name">
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Jane Doe"
            className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </Field>
        <Field label="Email" hint={type === "staff" ? "required for staff" : "optional"}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jane@example.com"
            className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </Field>
        <Field
          label="Phone"
          hint={type === "dealer_agent" ? "E.164, e.g. +255700000000" : "optional"}
        >
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+255700000000"
            className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </Field>
        <Field
          label="Initial password"
          hint={
            type === "staff"
              ? "≥12 chars — share over a secure channel"
              : "optional, ≥12 — lets the agent sign in with phone + password (forced to change on first login)"
          }
        >
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </Field>
        {type === "dealer_agent" ? (
          <Field
            label="Dealer"
            hint="Scoping enforced server-side — agent only sees this dealer's customers/loans/devices."
          >
            <select
              value={dealerId}
              onChange={(e) => setDealerId(e.target.value)}
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              disabled={dealersQuery.isLoading}
            >
              <option value="">
                {dealersQuery.isLoading ? "Loading dealers…" : "Select dealer…"}
              </option>
              {(dealersQuery.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
      </div>

      <Field
        label="Initial roles"
        hint="Approval-requiring roles (platform_admin, …) must go through Approvals."
      >
        <div className="flex flex-wrap gap-2">
          {assignable.map((r) => {
            const selected = roleKeys.includes(r.key);
            return (
              <button
                key={r.key}
                type="button"
                onClick={() =>
                  setRoleKeys((prev) =>
                    selected ? prev.filter((k) => k !== r.key) : [...prev, r.key],
                  )
                }
                className={
                  "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs " +
                  (selected
                    ? "border-brand-300 bg-brand-50 text-brand-800"
                    : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50")
                }
              >
                <span>{r.key}</span>
                <span className="text-[10px] text-slate-500">·{r.permissionCount}</span>
              </button>
            );
          })}
        </div>
      </Field>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={mutation.isPending}>
          {mutation.isPending ? "Creating…" : "Create user"}
        </Button>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      {hint ? <span className="ml-1 text-[10px] text-slate-400">· {hint}</span> : null}
      <div className="mt-1">{children}</div>
    </label>
  );
}
